import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';

import getStringifiedObject from '@bluecodecom/utils/getStringifiedObject';

import { nativeService } from '../../service';
import {
  VasHostConfig,
  VasHostConfigContextValue,
} from './VasHostConfig.types';

export const VasHostConfigContext =
  createContext<VasHostConfigContextValue | null>(null);

const VasHostConfigProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<VasHostConfig>({
    currentLanguage: 'DE',
    hasSuggestionsEnabled: false,
    isDarkThemeEnabled: false,
    isLocationAccepted: false,
    isLocationEnabled: false,
    isPushNotificationAccepted: false,
    isSuggestionsTabEnabled: false,
    hasNfcPaymentActivated: false,
    isVasEnabled: false,
    sdkSessionId: null,
  });

  useEffect(() => {
    // Chained rather than assigned, because this provider is no longer the only
    // installer: libs/tracking installs its own handler at init to register the
    // `session_id` super property in every webview, not just the ones mounting
    // this provider (NAT-151). Whichever installs second must keep the first
    // alive — the alternative is an ordering rule nobody can see from either
    // file, and the loser gets silence rather than an error.
    const previous = window.setVasHostConfig;

    const handler = (payload: string) => {
      previous?.(payload);

      const data = getStringifiedObject(payload) as Partial<VasHostConfig>;

      setData((d) => ({
        ...d,
        ...data,
        // The SDK telemetry session id, registered downstream as the
        // `session_id` analytics super property. Normalised because the host
        // sends `''` before a session exists and SDK builds older than the one
        // that added the field omit it entirely — neither may leak into the
        // context, and a later payload without it must not wipe a known id.
        sdkSessionId: data.sdkSessionId || d.sdkSessionId || null,
      }));

      setIsLoading(false);
    };

    window.setVasHostConfig = handler;

    nativeService.requestVasHostConfig();

    // Uninstall on unmount, which chaining made necessary and assignment did
    // not: an overwriting handler was *replaced* by the next mount, while a
    // chaining one is captured as the next mount's `previous`. Without this,
    // every remount adds a link that dispatches into a dead closure, and each
    // host-config payload is then parsed once per mount that ever happened
    // (Greptile review of #8857).
    //
    // Restored only when we are still the installed handler. If something
    // chained onto us afterwards — libs/tracking, initialising later — its
    // handler is what `window.setVasHostConfig` holds, and replacing it with
    // `previous` would drop it: exactly the silence the chaining exists to
    // prevent. In that case we leave the chain alone; our own link stays but
    // does not grow, and a `setData` on an unmounted component is a no-op.
    return () => {
      if (window.setVasHostConfig === handler) {
        window.setVasHostConfig = previous;
      }
    };
  }, []);

  return (
    <VasHostConfigContext.Provider value={{ isLoading, ...data }}>
      {children}
    </VasHostConfigContext.Provider>
  );
};

export default VasHostConfigProvider;
